/** "Reload" on "New version available" (06 §15.3). */

/** How long "Reload" waits for the new worker to take over before it reloads anyway. */
export const TAKEOVER_MS = 3000;

/** What `applyUpdate` uses (the browser's; fakes in tests). */
export interface UpdateDeps {
  readonly sw: Pick<ServiceWorkerContainer, 'getRegistration' | 'addEventListener' | 'removeEventListener'> | undefined;
  readonly reload: () => void;
  readonly wait: (ms: number) => Promise<void>;
}

const browserDeps = (): UpdateDeps => ({
  sw: 'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
  reload: () => location.reload(),
  wait: (ms) => new Promise((r) => setTimeout(r, ms)),
});

/**
 * "Reload" (06 §15.3): tells the waiting worker to take over (`SKIP_WAITING`), waits until it controls the page or at
 * most `TAKEOVER_MS`, then always reloads. Workbox's own update function reloads only on a `controlling` event, which
 * never comes when the page is not controlled (after a hard reload, Cmd-Shift-R) or when no worker is waiting any more
 * (another tab took the update): the button then did nothing (Alexander, 2026-10-03).
 */
export async function applyUpdate(deps: UpdateDeps = browserDeps()): Promise<void> {
  const reg = await deps.sw?.getRegistration().catch(() => undefined);
  const waiting = reg?.waiting;
  if (deps.sw && waiting) {
    const sw = deps.sw;
    let onChange: (() => void) | null = null;
    const tookOver = new Promise<void>((resolve) => {
      onChange = () => resolve();
      sw.addEventListener('controllerchange', onChange);
      waiting.addEventListener('statechange', () => waiting.state === 'activated' && resolve());
    });
    waiting.postMessage({ type: 'SKIP_WAITING' });
    await Promise.race([tookOver, deps.wait(TAKEOVER_MS)]);
    if (onChange) {
      sw.removeEventListener('controllerchange', onChange);
    }
  }
  deps.reload();
}
